/**
 * A key played on a MIDI keyboard, as the note to write where the caret stands.
 *
 * A MIDI key is a pitch that sounds and nothing else. It carries no spelling -- the black
 * key between F and G is F sharp or G flat and the keyboard does not know which -- and it is
 * not the pitch a transposing part writes. Both are settled here, and both are settled
 * against what is on the screen rather than against the stored document, because someone
 * playing at the page has to see the note they played appear where they expected it.
 *
 * - **The part's transposition is taken out.** Middle C played into a clarinet in B flat is
 *   written as the D that sounds it, and into a piccolo as the C an octave below. This is
 *   what MuseScore and Sibelius both do, and it is the only reading under which a part can
 *   be played in from an ordinary keyboard at all.
 * - **The spelling comes from the signature the staff prints.** A black key in three sharps
 *   is F sharp and in three flats is G flat. Nothing here can do better than that; a player
 *   who wanted the other spelling respells the note afterwards, which is one keystroke.
 * - **On a drum staff the key names the drum.** A pad sends the General MIDI number of the
 *   instrument it is, so it writes that instrument rather than whatever is written on the
 *   line that number would otherwise fall on. A pad the kit has no drum for writes nothing,
 *   and says so.
 */

import { partKey } from "../document/key";
import { drumByMidi } from "../document/percussion";
import { AT_PITCH, chromaticShift, drumHeadOf, spell } from "../document/pitch";
import { instrumentAt, measureIndexOf, partById, resolveAttributes } from "../document/query";
import type { Instrument, Part, Score } from "../document/types";
import type { TypedStaff } from "./entry-spell";
import { staffAt, typedPitch } from "./entry-spell";
import type { EntryState } from "./entry-write";
import type { Sounded } from "./entry-note";

/**
 * The signature the staff under the caret prints, in fifths.
 *
 * It is the part's own signature, not the piece's, because that is what the notes beside
 * the played one are spelled against. A concert-pitch score is the exception and needs no
 * conversion: every part of it prints the piece's key, which is what makes it a score a
 * conductor can read down.
 */
function printedFifths(
  score: Score,
  state: EntryState,
  instrument: Instrument | null,
  staff: TypedStaff,
): number {
  const index = state.caret === null ? 0 : measureIndexOf(score, state.caret.measureId);
  const { key } = resolveAttributes(score, Math.max(index, 0));
  return instrument === null || staff.heard !== null ? key.fifths : partKey(key, instrument).fifths;
}

/** What the player has in their hands where the caret stands, doublings and all. */
function heldAt(score: Score, state: EntryState, part: Part | null): Instrument | null {
  if (part === null) return null;
  const index = state.caret === null ? 0 : measureIndexOf(score, state.caret.measureId);
  return instrumentAt(score, part, Math.max(index, 0));
}

/**
 * The note a played key means on the staff the caret is on, or the reason it means none.
 *
 * The arithmetic is in semitones and is the mirror of what the screen is showing. What is
 * drawn is the written pitch, unless a concert-pitch score is on the screen, in which case
 * what is drawn is the written pitch with the part's chromatic transposition taken out --
 * and only the chromatic part, because that reading leaves an octave-transposing part where
 * its reader expects it. The letters are then spelled in that reading and handed to the same
 * function a typed letter goes through, which puts the transposition back and finds the
 * fret.
 */
export function playedSound(
  score: Score,
  state: EntryState,
  note: number,
  tieStop: boolean,
): Sounded | string {
  const partId = state.caret?.partId ?? null;
  const staff = staffAt(score, partId, state.caret?.measureId ?? null, state.concert);

  if (staff.kit !== null) {
    const drum = drumByMidi(staff.kit, note);
    if (drum === null) return "no drum in this kit is played by that key";
    return {
      pitch: { ...drumHeadOf(drum.step, drum.octave, drum.name), tieStop },
      step: drum.step,
      octave: drum.octave,
    };
  }

  const part = partId === null ? null : partById(score, partId);
  const held = heldAt(score, state, part);
  const transposition = held?.transposition ?? AT_PITCH;
  const shown = note - chromaticShift(transposition) + (staff.heard?.chromatic ?? 0);
  const spelled = spell(shown, printedFifths(score, state, held, staff));
  const typed = { step: spelled.step, alter: spelled.alter, octave: spelled.octave };
  return {
    pitch: typedPitch(typed, spelled.octave, tieStop, staff),
    step: spelled.step,
    octave: spelled.octave,
  };
}
